import React from "react";
import { DsokCustomerDetailDataContext } from "./DsokCustomerDetailContext";

/**
 * Hook for DSOK customer-detail subpages to access shared customer data,
 * loading/error state, refetch, dirty state, and section update payload registration.
 *
 * Also exposes derived helpers:
 * - dsokSettings: raw DSOK settings object from API
 * - easyServices: array of enabled Easy services (e.g. ["easy-buy"])
 * - hasEasyBuy: boolean flag, true iff easyServices contains "easy-buy"
 *
 * @returns {{
 *   customerUrl: string,
 *   data: any,
 *   customer: any,
 *   loading: boolean,
 *   error: any,
 *   refetch: function,
 *   isDirty: boolean,
 *   setDirty: function,
 *   resetDirty: function,
 *   originalDsokSettings: any,
 *   setOriginalDsokSettings: function,
 *   registerSectionUpdatePayload: function,
 *   unregisterSectionUpdatePayload: function,
 *   getSectionUpdatePayload: function,
 *   dsokSettings: any,
 *   easyServices: string[] | undefined,
 *   hasEasyBuy: boolean
 * }}
 */
export function useDsokCustomerDetail() {
  const ctx = React.useContext(DsokCustomerDetailDataContext);

  const dsokSettings = ctx?.data?.dsokSettings ?? ctx?.originalDsokSettings ?? null;

  const rawEasyServices =
    (dsokSettings && dsokSettings.easyServices) ||
    (dsokSettings && dsokSettings.settings && dsokSettings.settings.easyServices) ||
    undefined;

  const easyServices = Array.isArray(rawEasyServices)
    ? rawEasyServices
    : rawEasyServices != null
    ? [rawEasyServices]
    : undefined;
  const hasEasyBuy = !!(easyServices && easyServices.includes("easy-buy"));

  return {
    customerUrl: ctx.customerUrl,
    data: ctx.data,
    customer: ctx.data,
    loading: ctx.loading,
    error: ctx.error,
    refetch: ctx.refetch,
    isDirty: ctx.isDirty ?? false,
    setDirty: ctx.setDirty ?? (() => {}),
    resetDirty: ctx.resetDirty ?? (() => {}),
    originalDsokSettings: ctx.originalDsokSettings ?? null,
    setOriginalDsokSettings: ctx.setOriginalDsokSettings ?? (() => {}),
    registerSectionUpdatePayload: ctx.registerSectionUpdatePayload ?? (() => {}),
    unregisterSectionUpdatePayload: ctx.unregisterSectionUpdatePayload ?? (() => {}),
    getSectionUpdatePayload: ctx.getSectionUpdatePayload ?? (() => null),
    dsokSettings,
    easyServices,
    hasEasyBuy,
  };
}
